// The Rally Point: TROOPS (the live game's ledger: totals, support here, your support abroad) · COMMANDS (target, sliders, the server's travel
// preview, ATTACK / SUPPORT) · MOVEMENT (the live game's
// incoming / outgoing list; tap a row for its Movement Details: times, origin, target, morale, troops, cancel / reveal).
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { RallyPrepareTarget } from "@/api/types";
import { art } from "@/kit/art";
import { Countdown, GoldRange, ScreenHero, UnitMedal, XYField } from "@/kit/city";
import { IconBookmark, IconCheck, IconChevronDown, IconChevronRight, IconClose, IconCrossedSwords, IconEye, IconShield } from "@/kit/icons";
import { Confirm, Sheet } from "@/kit/overlays";
import { Button, Card, CardHead, Disc, Empty, Row, RowText, Seg, Tabs, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { useRallyTarget } from "@/state/rallyTarget";
import { lastAttackKey, useRallyLastAttack } from "@/state/rallyLastAttack";
import { TROOP_KEYS, TROOP_NAMES, type TroopKey } from "@/game/constants";
import { fmtCoords, fmtNum, parseCoords, plural } from "@/game/format";
import { fmtClock, fmtDuration, fmtStampUTC, parseHMS } from "@/game/time";
import { movementCard } from "@/game/share";
import { DiscordShareButton } from "@/kit/DiscordShare";
import { UNITS, fieldsBetween } from "@/game/rules";
import { cityCoords, garrison, homeCount, movements, owned, researchLevel, sumTroops, travelPreview, type MoveEntry } from "@/game/city";
import { CityPage, useCity } from "./CityRoute";
import { marksAt, marksWarning, useVtMarks } from "@/state/vt";
import { SavedTargets } from "./Targets";

type Tab = "troops" | "commands" | "movement";

export function Rally() {
  const { tab: tabP } = useParams();
  const { v, base } = useCity();
  const nav = useNavigate();
  const tab = (["troops", "commands", "movement"].includes(tabP ?? "") ? tabP : "troops") as Tab;
  const setTab = (t: Tab) => nav(`${base}/rally/${t}`, { replace: true });
  if (!v) return <CityPage hud={false}>{null}</CityPage>;
  const g = garrison(v);
  const abroad = sumTroops(v.total_troops_sent);
  const mv = movements(v);
  const incomingAttacks = mv.incoming.filter((m) => m.kind === "attack").length;
  const outgoing = mv.outgoing.filter((m) => m.kind === "attack" || m.kind === "support").length;
  const movementBadge = incomingAttacks + outgoing;
  return (
    <CityPage hud={false}>
      <div className="stack">
        <ScreenHero
          title="Rally Point"
          art={art.hero.rally}
          artWidth="56%"
          onBack={() => nav(base)}
          onGear={() => nav(`${base}/hall`)}
          gearLabel="City overview"
          stats={[
            { label: "At home", value: fmtNum(g.troops) },
            { label: "Support abroad", value: fmtNum(abroad) },
            { label: "Incoming", value: incomingAttacks, tone: incomingAttacks ? ("crim" as const) : undefined },
            { label: "Outgoing", value: outgoing },
          ]}
        />
        <Seg value={tab} onChange={setTab} options={[{ value: "troops", label: "Troops" }, { value: "commands", label: "Commands" }, { value: "movement", label: "Movement", badge: movementBadge || undefined }]} />
        {tab === "troops" && <TroopsTab />}
        {tab === "commands" && <CommandsTab />}
        {tab === "movement" && <MovementTab />}
      </div>
    </CityPage>
  );
}

// ------------------------------------------------------------------ TROOPS

function TroopsTab() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const [sub, setSub] = useState<"here" | "abroad">("here");
  // the live game's multi-select: tick sender rows, then one Send Troops Back / Withdraw Troops for all of them
  const [picked, setPicked] = useState<Set<number>>(() => new Set());
  const [confirm, setConfirm] = useState<"sr" | "ss" | null>(null);
  if (!v) return null;
  const here = v.support?.received ?? [];
  const abroad = v.support?.sent ?? [];
  const list = sub === "here" ? here : abroad;
  const type: "sr" | "ss" = sub === "here" ? "sr" : "ss";
  const chosen = list.filter((s) => picked.has(s.id));
  const toggle = (id: number) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = list.length > 0 && chosen.length === list.length;
  const world = base.replace(/\/city\/[^/]+$/, "");
  const switchSub = (s: "here" | "abroad") => { setSub(s); setPicked(new Set()); };

  return (
    <>
      {/* Oct 11 2026 (Ben): the live game's ledger layout — one row of troop icons, then labelled rows of nine counts — in
          the new style */}
      <Card className="ledger">
        <LedgerIcons />
        <LedgerLabel aside={`${fmtNum(sumTroops(v.troops_total))} troops`}>Total troops for this city</LedgerLabel>
        <LedgerRow counts={v.troops_total} />
      </Card>
      <Tabs value={sub} onChange={switchSub} options={[{ value: "here", label: "Support in this city" }, { value: "abroad", label: "My support abroad" }]} />
      <Card className="ledger">
        {sub === "here" ? (
          <>
            <LedgerLabel>Total troops (foreign and native)</LedgerLabel>
            <LedgerRow counts={v.foreign_and_native_troops} />
            <LedgerLabel>From this city</LedgerLabel>
            <LedgerRow counts={v.troops_in_village} />
            <LedgerLabel aside={here.length ? `from ${plural(here.length, "city", "cities")}` : undefined}>Total foreign</LedgerLabel>
            <LedgerRow counts={v.total_troops_received} />
          </>
        ) : (
          <>
            <LedgerLabel aside={abroad.length ? `in ${plural(abroad.length, "city", "cities")}` : undefined}>Total troops abroad</LedgerLabel>
            <LedgerRow counts={v.total_troops_sent} />
          </>
        )}
        {list.map((s) => {
          const d = sub === "here" ? s.origin_village_data : s.target_village_data;
          const coords = sub === "here" ? s.origin_coords : s.target_coords;
          const xy = parseCoords(d?.coords ?? coords);
          const on = picked.has(s.id);
          return (
            <div key={s.id} className={cx("ledger-sup", on && "on")}>
              <div className="ledger-label sup">
                <button type="button" className={cx("ledger-check", on && "on")} role="checkbox" aria-checked={on} aria-label={`Select ${d?.city_name ?? coords}`} onClick={() => toggle(s.id)}>
                  {on && <IconCheck />}
                </button>
                <button type="button" className="ledger-city" onClick={() => (xy ? nav(`${world}/map?x=${xy[0]}&y=${xy[1]}`) : undefined)}>
                  <b>{d?.city_name ?? "City"}</b> <span>{fmtCoords(d?.coords ?? coords)}{d?.continent !== undefined ? ` · R${d.continent}` : ""}</span>
                </button>
                <span className="aside">{fmtNum(sumTroops(s.units))}</span>
              </div>
              <LedgerRow counts={s.units} />
            </div>
          );
        })}
        {list.length > 0 ? (
          <div className="card-foot ledger-foot">
            <button type="button" className="link caps" onClick={() => setPicked(allOn ? new Set() : new Set(list.map((s) => s.id)))}>{allOn ? "Clear" : "Select all"}</button>
            <Button size="sm" kind="steel" disabled={!chosen.length} onClick={() => setConfirm(type)}>
              {type === "sr" ? "Send troops back" : "Withdraw troops"}{chosen.length ? ` (${chosen.length})` : ""}
            </Button>
          </div>
        ) : (
          <div className="c-note left">{sub === "here" ? `No allied troops are stationed in ${v.village?.city_name ?? "this city"}.` : "None of your troops are supporting another city."}</div>
        )}
      </Card>
      <Confirm open={!!confirm} onClose={() => setConfirm(null)} title={confirm === "sr" ? "Send these troops back?" : "Withdraw your troops?"}
        text={`${fmtNum(chosen.reduce((a, s) => a + sumTroops(s.units), 0))} troops from ${plural(chosen.length, "city", "cities")} march ${confirm === "sr" ? "back to their own cities" : "home"}.`}
        action={confirm === "sr" ? "Send back" : "Withdraw"} kind="steel" busy={actions.isBusy("recall")}
        onConfirm={async () => {
          if (!confirm || !chosen.length) return;
          const r = await actions.run("recall", () => api.rally.recall({ wid, vid }, chosen.map((s) => s.id), confirm), { success: "Troops are on their way." });
          if (r) { setConfirm(null); setPicked(new Set()); }
        }} />
    </>
  );
}

/** The ledger's column heads: the nine troop icons, one per column (the live game's Rally Troops screen). */
const LedgerIcons = () => (
  <div className="ledger-row ledger-icons" role="row">
    {TROOP_KEYS.map((k) => (
      <span key={k} className="ledger-ic" role="columnheader" title={TROOP_NAMES[k]}><UnitMedal troop={k} tier={0} size={80} /></span>
    ))}
  </div>
);

const LedgerLabel = ({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) => (
  <div className="ledger-label"><span className="t">{children}</span>{aside !== undefined && <span className="aside">{aside}</span>}</div>
);

/** Nine counts under the icons; zeros dimmed, five- and six-digit counts set smaller so a column never overflows. */
const LedgerRow = ({ counts, dash }: { counts?: Partial<Record<TroopKey, number>> | null; dash?: boolean }) => (
  <div className="ledger-row" role="row">
    {TROOP_KEYS.map((k) => {
      const n = Number(counts?.[k] ?? 0) || 0;
      const s = !n && dash ? "-" : String(Math.round(n)); // plain digits, as the live game prints them: 2488 stays full size in a narrow column
      return <span key={k} role="cell" aria-label={`${TROOP_NAMES[k]} ${Math.round(n)}`} className={cx("ledger-n", !n && "zero")} data-len={s.length >= 6 ? "xl" : s.length >= 5 ? "l" : undefined}>{s}</span>;
    })}
  </div>
);

// ------------------------------------------------------------------ COMMANDS

function CommandsTab() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  // the map's ATTACK / SEND SUPPORT arrive as ?x=&y=(&mode=s)
  const [params] = useSearchParams();
  // the target: the map's ?x=&y= when given, else the last target entered in this world (kept after sending a command
  // and across city switches — see state/rallyTarget)
  const remembered = useRallyTarget((s) => s.byWorld[String(wid)]);
  const rememberTarget = useRallyTarget((s) => s.set);
  const fromUrl = (k: "x" | "y") => (params.get(k) ?? "").replace(/[^\d]/g, "").slice(0, 4);
  const [x, setXState] = useState(() => (fromUrl("x") && fromUrl("y") ? fromUrl("x") : remembered?.x ?? ""));
  const [y, setYState] = useState(() => (fromUrl("x") && fromUrl("y") ? fromUrl("y") : remembered?.y ?? ""));
  const setXY = (nx: string, ny: string) => { setXState(nx); setYState(ny); rememberTarget(wid, { x: nx, y: ny }); };
  // a target arriving from the map becomes the remembered one too
  useEffect(() => {
    if (fromUrl("x") && fromUrl("y")) rememberTarget(wid, { x: fromUrl("x"), y: fromUrl("y") });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sel, setSel] = useState<Partial<Record<TroopKey, number>>>({});
  // Ben, Oct 15 2026: Commands opens with the troops of this city's last attack — each type the player sent part of, at
  // the same number, or all at home when there are fewer (types sent in full aren't carried; see state/rallyLastAttack)
  const lastAttack = useRallyLastAttack((s) => s.byCity[lastAttackKey(wid, vid)]);
  const rememberAttack = useRallyLastAttack((s) => s.set);
  const prefilled = useRef<string | null>(null);
  // Oct 15 2026: a launch from the Valor Tools Planner ("Send ›") brings its troops as ?u=t_lc:400,t_r:25 and its launch time
  // as ?lb=<ms>; those troops win over the last attack's (each capped at what's home)
  const urlUnits = useMemo(() => unitsFromParam(params.get("u")), [params]);
  const launchBy = Number(params.get("lb")) || 0;
  useEffect(() => {
    if (!v || prefilled.current === lastAttackKey(wid, vid)) return;
    prefilled.current = lastAttackKey(wid, vid);
    const next: Partial<Record<TroopKey, number>> = {};
    const want = urlUnits ?? lastAttack;
    for (const k of TROOP_KEYS) {
      const n = Math.min(want?.[k] ?? 0, homeCount(v, k));
      if (n > 0) next[k] = n;
    }
    setSel(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v, wid, vid]);
  const [saved, setSaved] = useState(false);
  const [confirm, setConfirm] = useState<"a" | "s" | null>(null);
  const supportFirst = params.get("mode") === "s";
  const [debounced, setDebounced] = useState<string | null>(null);
  const target = x && y ? `${Number(x)},${Number(y)}` : null;
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(target), 500);
    return () => window.clearTimeout(t);
  }, [target]);
  const selectedKeys = TROOP_KEYS.filter((k) => (sel[k] ?? 0) > 0);
  // Valor Tools (switched on for the player): the guild's claims and op targets, for the send confirm's warning
  const vtMarks = useVtMarks(wid);
  const prep = useQuery({
    queryKey: ["rally-prepare", wid, vid, debounced, selectedKeys.join(",")],
    queryFn: () => api.rally.prepare({ wid, vid }, debounced!, selectedKeys.length ? selectedKeys : ["t_sf"]),
    enabled: !!debounced && !!v,
    staleTime: 30_000,
    retry: false,
  });
  if (!v) return null;
  const from = cityCoords(v);
  const to = parseCoords(target);
  const details = debounced ? prep.data?.city_details?.[debounced] : undefined;
  const tv: RallyPrepareTarget | undefined = details?.target_village;
  const dist = from && to ? fieldsBetween(from, to) : null;
  const preview = from && to ? travelPreview(from, to, sel) : null;
  const serverMs = tv ? parseHMS(tv.arrival_in) : NaN;
  const travelMs = selectedKeys.length && Number.isFinite(serverMs) ? serverMs : preview?.ms ?? NaN;
  const pace = preview?.pace;
  const total = sumTroops(sel);
  const own = from && to && from[0] === to[0] && from[1] === to[1];
  const setN = (k: TroopKey, n: number) => setSel((s) => ({ ...s, [k]: Math.max(0, Math.min(homeCount(v, k), Math.floor(n))) }));
  const all = () => setSel(Object.fromEntries(TROOP_KEYS.map((k) => [k, homeCount(v, k)])));
  const clear = () => setSel({});
  const name = tv?.city_name?.replace(/<[^>]+>/g, "").replace(/\(Barbarian\)/i, "").trim();
  const sendable = total > 0 && !!to && !own;

  return (
    <>
      {/* the target and the send buttons float at the top while the troop list scrolls under them */}
      <Card className="rally-target">
        <div className="target-row">
          <h2>Target</h2>
          <XYField x={x} y={y} onChange={(nx, ny) => setXY(nx, ny)} />
          <Disc size="sm" label="Saved targets" onClick={() => setSaved(true)}><IconBookmark /></Disc>
          <button type="button" className="link caps" onClick={() => setSaved(true)}>Saved <IconChevronDown style={{ width: 12, height: 12 }} /></button>
        </div>
        <div className="target-line">
          {!to ? <span className="muted">Enter the target's X and Y, or pick a saved target.</span>
            : own ? <span className="crimtxt">That is this city.</span>
            : prep.isFetching && !tv ? <span className="muted">Looking up {fmtCoords(to)}…</span>
            : tv ? <><b className="gold">{name}</b> {fmtCoords(to)} · {dist?.toFixed(2)} fields{tv.is_barbarian ? " · barbarian" : details?.target_char ? ` · ${details.target_char.login}` : ""}{tv.morale && tv.morale < 100 ? <span className="crimtxt"> · morale {tv.morale}%</span> : null}</>
            : prep.isError ? <span className="crimtxt">No city at {fmtCoords(to)}.</span>
            : <span className="muted">{fmtCoords(to)} · {dist?.toFixed(2)} fields</span>}
        </div>
        {launchBy > 0 && (
          <div className="target-line vt-launch">
            <span>Valor Tools Planner: launch at <b className="gold">{fmtClock(launchBy, true)}</b> UTC
              {launchBy > now ? <> · in <b>{fmtDuration(launchBy - now)}</b></> : <span className="crimtxt"> · that time has passed</span>}</span>
          </div>
        )}
        <div className="rally-send">
          <div className="line">
            <span>{Number.isFinite(travelMs) && total > 0 ? <>Travel <b>{fmtDuration(travelMs)}</b> · arrives <b>{fmtClock(now + travelMs, true)}</b></> : <span className="muted">Select troops and a target</span>}</span>
            <span className="r">{pace ? `paced by ${TROOP_NAMES[pace]} · ${UNITS[pace].speed} min/field` : ""}</span>
          </div>
          <div className="acts">
            {supportFirst ? (
              <>
                <Button kind="steel" className="support" disabled={!sendable} onClick={() => setConfirm("s")}>Support</Button>
                <Button kind="crimson" disabled={!sendable} onClick={() => setConfirm("a")}>Attack</Button>
              </>
            ) : (
              <>
                <Button kind="crimson" disabled={!sendable} onClick={() => setConfirm("a")}>Attack</Button>
                <Button kind="steel" disabled={!sendable} onClick={() => setConfirm("s")}>Support</Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <CardHead title="Troops" aside={`${fmtNum(total)} selected`}>
          <Button size="sm" onClick={all}>All</Button>
          <Button size="sm" onClick={clear}>Clear</Button>
        </CardHead>
        <div className="rows">
          {TROOP_KEYS.map((k) => {
            const home = homeCount(v, k);
            const n = sel[k] ?? 0;
            return (
              <Row key={k} className={cx("troop-row", !home && "dim", n > 0 && "goldedge")}>
                <UnitMedal troop={k} tier={researchLevel(v, k)} size={54} dim={!home} />
                <RowText line={<b className={cx(home ? "creamt" : "muted")} style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>{TROOP_NAMES[k]}</b>} meta={home ? `${fmtNum(home)} home` : "none home"} />
                <GoldRange value={n} max={home} onChange={(val) => setN(k, val)} label={`${TROOP_NAMES[k]} to send`} disabled={!home} />
                <input type="text" inputMode="numeric" className="c-num" style={{ width: "calc(var(--u) * 110)" }} value={n ? String(n) : ""} placeholder="0" disabled={!home} aria-label={`${TROOP_NAMES[k]} count`}
                  onChange={(e) => setN(k, Number(e.target.value.replace(/[^\d]/g, "") || 0))} />
                <button type="button" className="link caps" disabled={!home} onClick={() => setN(k, home)}>Max</button>
              </Row>
            );
          })}
        </div>
      </Card>


      <SavedTargets open={saved} onClose={() => setSaved(false)} onPick={(c) => { const p = parseCoords(c); if (p) setXY(String(p[0]), String(p[1])); setSaved(false); }} />
      {/* the send confirm (Ben, Oct 14 2026): laid out like the Movement Details card — "From (x, y) attacks / supports
          To (x, y)", then march time, arrival time (UTC, as the card prints it), morale (attacks only) and the troop ledger */}
      <SendConfirm open={!!confirm} kind={confirm ?? "a"} onClose={() => setConfirm(null)}
        from={{ name: v.village?.city_name ?? "Your city", coords: from }} to={{ name: name || null, coords: to }}
        travelMs={travelMs} arriveMs={now + travelMs} morale={tv?.morale} units={sel} total={total} busy={actions.isBusy("send")}
        warn={to && vtMarks.data?.on ? marksWarning(marksAt(vtMarks.data, to[0], to[1]), confirm !== "s") : []}
        onConfirm={async () => {
          if (!to || !confirm) return;
          // what's remembered for next time: the types sent in part (an attack only; support leaves it as it was)
          const partial: Partial<Record<TroopKey, number>> = {};
          for (const k of TROOP_KEYS) { const n = sel[k] ?? 0; if (n > 0 && n < homeCount(v, k)) partial[k] = n; }
          const r = await actions.run("send", () => api.rally.send({ wid, vid }, { target_x: to[0], target_y: to[1], rally_key: confirm, units: sel }));
          if (r) { if (confirm === "a") rememberAttack(wid, vid, partial); setConfirm(null); setSel({}); nav(`${base}/rally/movement`); }
        }} />
    </>
  );
}

/** "t_lc:400,t_r:25" (the Valor Tools Planner's Send ›) → troop counts; null when there's none to read. */
export function unitsFromParam(v: string | null): Partial<Record<TroopKey, number>> | null {
  if (!v) return null;
  const out: Partial<Record<TroopKey, number>> = {};
  for (const part of v.split(",").slice(0, 12)) {
    const m = /^(t_[a-z]{1,3}):(\d{1,9})$/.exec(part.trim());
    if (m && (TROOP_KEYS as readonly string[]).includes(m[1]) && +m[2] > 0) out[m[1] as TroopKey] = +m[2];
  }
  return Object.keys(out).length ? out : null;
}

function SendConfirm({ open, kind, onClose, from, to, travelMs, arriveMs, morale, units, total, busy, onConfirm, warn = [] }: {
  open: boolean; kind: "a" | "s"; onClose: () => void;
  from: { name: string; coords: [number, number] | null }; to: { name: string | null; coords: [number, number] | null };
  travelMs: number; arriveMs: number; morale?: number; units: Partial<Record<TroopKey, number>>; total: number; busy?: boolean; onConfirm: () => void;
  /** Valor Tools (Oct 15 2026): claimed by a guildmate, in a guild op */
  warn?: string[];
}) {
  const attack = kind === "a";
  const known = Number.isFinite(travelMs);
  const mor = Number(morale);
  return (
    <Sheet open={open} onClose={onClose} label={attack ? "Confirm attack" : "Confirm support"}>
      <div className={cx("dialog send-confirm", attack ? "attack" : "support")} style={{ padding: 0 }}>
        <h3 className="sc-eyebrow">{attack ? "Confirm attack" : "Confirm support"}</h3>
        <div className="sc-route">
          <span className="sc-medal" aria-hidden="true">{attack ? <IconCrossedSwords /> : <IconShield />}</span>
          <p>
            <b>{from.name}</b> <span className="sc-xy">{fmtCoords(from.coords)}</span> <i>{attack ? "attacks" : "supports"}</i>{" "}
            {to.name ? <><b>{to.name}</b> <span className="sc-xy">{fmtCoords(to.coords)}</span></> : <b>{fmtCoords(to.coords)}</b>}
          </p>
        </div>
        <dl className="mvl-facts sc-facts">
          <div><dt>March time:</dt><dd className="mvl-count">{known ? fmtDuration(travelMs) : "—"}</dd></div>
          <div><dt>Arrival time:</dt><dd>{known ? fmtStampUTC(arriveMs) : "—"}</dd></div>
          {attack && <div><dt>Morale:</dt><dd>{Number.isFinite(mor) && morale !== undefined ? `${Math.round(mor)} %` : "—"}</dd></div>}
          <div><dt>Troops:</dt><dd className="mvl-count">{fmtNum(total)}</dd></div>
        </dl>
        <div className="ledger mvl-ledger sc-ledger">
          <LedgerIcons />
          <LedgerRow counts={units} dash />
        </div>
        {warn.length > 0 && (
          <div className="sc-warn" role="note">
            {warn.map((w, i) => <p key={i} className={cx(i === 0 && /^Claimed/.test(w) ? "crimtxt" : "gold")}>{w}</p>)}
          </div>
        )}
        <div className="acts sc-acts">
          <Button kind="steel" size="md" onClick={onClose}>Cancel</Button>
          <Button kind={attack ? "crimson" : "steel"} size="md" className={cx(!attack && "support")} onClick={onConfirm} busy={busy}>{attack ? "Attack" : "Send support"}</Button>
        </div>
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ MOVEMENT

function MovementTab() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  const [sub, setSub] = useState<"in" | "out">("out");
  // Oct 13 2026 (Ben): the live game's Movement list (one row per army: verb, target, coords, "Arrival In"), in the new
  // style. Tapping a row anywhere but its cancel button opens the live game's Movement Details right under it; several
  // can be open at once.
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [cancel, setCancel] = useState<MoveEntry | null>(null);
  const [reveal, setReveal] = useState<MoveEntry | null>(null);
  const mv = useMemo(() => (v ? movements(v) : { incoming: [], outgoing: [] }), [v]);
  useEffect(() => {
    if (mv.incoming.some((m) => m.kind === "attack")) setSub("in");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!v) return null;
  const inAttacks = mv.incoming.filter((m) => m.kind === "attack");
  const outActive = mv.outgoing.filter((m) => m.kind === "attack" || m.kind === "support");
  const list = sub === "out" ? mv.outgoing : mv.incoming; // already soonest first
  const revealOwned = owned(v, "reveal_incoming");
  const world = base.replace(/\/city\/[^/]+$/, "");
  const keyOf = (e: MoveEntry) => `${e.dir}-${e.m.id}`;
  const toggle = (k: string) => setOpen((o) => { const n = new Set(o); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toMap = (coords: string) => { const xy = parseCoords(coords); if (xy) nav(`${world}/map?x=${xy[0]}&y=${xy[1]}`); };

  const verbOf = (e: MoveEntry) =>
    e.dir === "out"
      ? e.kind === "attack" ? "Attacking" : e.kind === "support" ? "Supporting" : "Returning from"
      : e.kind === "attack" ? "Attack from" : e.kind === "support" ? "Support from" : "Returning from";
  const toneOf = (e: MoveEntry) => (e.dir === "in" && e.kind === "attack" ? "crim" : e.kind === "support" ? "green" : undefined);
  const canCancel = (e: MoveEntry) => e.dir === "out" && (e.kind === "attack" || e.kind === "support");

  // a render function, not an inner component (an inner component remounts every row on each clock tick)
  const moveItem = (e: MoveEntry) => {
    const k = keyOf(e);
    const isOpen = open.has(k);
    const tone = toneOf(e);
    const name = `${verbOf(e)} ${e.other.name} ${fmtCoords(e.other.coords)}`;
    const target = { name: e.m.target_name, coords: e.m.target_coords };
    const isReturn = e.kind.startsWith("return");
    const landLabel = isReturn ? "Returning in" : e.kind === "support" ? "Support arriving in" : "Attack landing in";
    const cancelLabel = e.kind === "support" ? "Cancel support" : "Cancel attack";
    return (
      <div key={k} className={cx("mvl-item", isOpen && "open", tone)}>
        {/* the whole head toggles; the cancel button stops the tap so it only cancels */}
        <div className="mvl-head" onClick={() => toggle(k)}>
          {canCancel(e) && (
            <button type="button" className="mvl-x" aria-label={`${cancelLabel}: ${e.other.name}`} title={cancelLabel}
              onClick={(ev) => { ev.stopPropagation(); setCancel(e); }}>
              <IconClose />
            </button>
          )}
          <button type="button" className="mvl-toggle" aria-expanded={isOpen} aria-label={`${name} — ${isOpen ? "hide" : "show"} details`}
            onClick={(ev) => { ev.stopPropagation(); toggle(k); }}>
            <span className="mvl-txt">{verbOf(e)} <b>{e.other.name}</b> <span className="mvl-xy">{fmtCoords(e.other.coords)}</span></span>
            <span className="mvl-time"><Countdown endMs={e.endMs} done="Landed" /></span>
            <span className="mvl-chev" aria-hidden><IconChevronRight /></span>
          </button>
        </div>
        {isOpen && (
          <div className="mvl-detail" role="region" aria-label={`Movement details: ${name}`}>
            <dl className="mvl-facts">
              <div><dt>{landLabel}:</dt><dd className={cx("mvl-count", tone === "crim" && "crimtxt")}><Countdown endMs={e.endMs} done="Landed" /></dd></div>
              <div><dt>Arrival time:</dt><dd>{fmtStamp(e.endMs)}</dd></div>
              <div><dt>Origin:</dt><dd><button type="button" className="mvl-link" onClick={() => toMap(e.m.origin_coords)}>{e.m.origin_name} {fmtCoords(e.m.origin_coords)} <IconChevronRight /></button></dd></div>
              <div><dt>Target:</dt><dd><button type="button" className="mvl-link" onClick={() => toMap(target.coords)}>{target.name} {fmtCoords(target.coords)} <IconChevronRight /></button></dd></div>
              {e.kind === "attack" && Number.isFinite(Number(e.m.morale)) && <div><dt>Morale:</dt><dd>{Math.round(Number(e.m.morale))} %</dd></div>}
            </dl>
            {e.units ? (
              <div className="ledger mvl-ledger">
                <LedgerIcons />
                <LedgerRow counts={e.units} dash />
              </div>
            ) : e.dir === "in" ? (
              <div className="mvl-hidden">
                <span>The troops in this army are hidden.</span>
                {e.kind === "attack" && <Button size="sm" kind="gold" onClick={() => setReveal(e)}><IconEye /> Reveal ({revealOwned})</Button>}
              </div>
            ) : null}
            {/* Share to Discord (Oct 13 2026): the movement and its landing time, into the guild's Discord */}
            <div className="mvl-actions">
              <DiscordShareButton wid={wid} card={() => movementCard(e, cityCoords(v))} />
              {canCancel(e) && <Button kind="steel" onClick={() => setCancel(e)}>{cancelLabel}</Button>}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Tabs value={sub} onChange={setSub} options={[
        { value: "in", label: <>Incoming{inAttacks.length ? <span className="badge inline">{inAttacks.length}</span> : null}</> },
        { value: "out", label: <>Outgoing{outActive.length ? <span className="badge inline">{outActive.length}</span> : null}</> },
      ]} />
      <Card className="mvl span-all">
        <div className="mvl-cols"><span>{sub === "out" ? "Outgoing troops" : "Incoming troops"}</span><span>Arrival in</span></div>
        {!list.length ? <Empty>{sub === "out" ? "No troops on the move." : "No armies coming — the roads are quiet."}</Empty> : list.map(moveItem)}
      </Card>
      <Confirm open={!!cancel} onClose={() => setCancel(null)} title="Turn this army around?" text={cancel ? `The ${cancel.kind} on ${cancel.other.name} is cancelled; the troops come home in ${fmtDuration(Math.max(0, now - cancel.startMs))}.${cancel.cancelUntilMs < now ? " The usual cancel window has passed — the game may refuse." : ""}` : ""}
        action="Cancel the army" busy={actions.isBusy("cancel_move")}
        onConfirm={async () => { if (!cancel) return; const r = await actions.run("cancel_move", () => api.rally.cancel({ wid, vid }, cancel.m.id), { success: "The army is turning back." }); if (r) setCancel(null); }} />
      <Confirm open={!!reveal} onClose={() => setReveal(null)} kind="gold" title="Reveal this army?" text={reveal ? `Spend one Reveal Incoming item (${revealOwned} owned) to see exactly what ${reveal.other.name} is sending. The result lands in your reports.` : ""}
        action={revealOwned ? "Reveal · 1 item" : "None owned"} busy={actions.isBusy("reveal")}
        onConfirm={async () => { if (!reveal || !revealOwned) return; const r = await actions.run("reveal", () => api.rally.revealIncoming({ wid, vid }, reveal.m.id, reveal.other.coords), { success: "Revealed — see your reports." }); if (r) setReveal(null); }} />
    </>
  );
}

/** The live game's Arrival Time: "10/13/2026 12:19:25" (UTC, like every clock in Valor Web). */
const fmtStamp = fmtStampUTC;
